// Archivo: src/components/Graphics.js
// Nota: los gráficos de Altitud/Ritmo/FC/Cadencia/Temperatura ya viven en
// ElevationChart.js ("Gráfico Consolidado de Actividad"), y el GAP vive en
// ElevationProfile.js ("Resumen de Altimetría"). Este componente aporta la
// tabla de vueltas (LapsTable) y el Histograma de FC por vuelta.
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler, Title } from 'chart.js';
import './styles/Graphics.css';
import LapsTable from './Dashboard/LapsTable';
import API_BASE_URL from '../config/api';
import { useTranslation } from '../i18n';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler, Title);

const Graphics = ({ fileId }) => {
  const { t } = useTranslation();
  const [laps, setLaps] = useState([]);

  const formatTime = (seconds) => {
    if (isNaN(seconds) || seconds < 0) return "00:00:00";
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const formatPace = useCallback((paceInMinutes) => {
    if (!paceInMinutes || paceInMinutes <= 0) return '00:00';
    const minutes = Math.floor(paceInMinutes);
    const seconds = Math.round((paceInMinutes - minutes) * 60);
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }, []);

  const calculatePace = useCallback((speed) => {
    if (!speed || speed <= 0) return "00:00";
    const paceInMinutes = 60 / speed;
    return formatPace(paceInMinutes);
  }, [formatPace]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/fit/parsed-data/${fileId}`);
        if (res.data?.laps) {
          const fitData = res.data;
          let accumulatedTime = 0;
          const lapsData = fitData.laps.map((lap, index) => {
            const totalElapsedTime = lap.total_elapsed_time || 0;
            accumulatedTime += totalElapsedTime;
            const speedKmh = (lap.enhanced_avg_speed || 0) * 3.6;
            return {
              vuelta: `${index + 1}`,
              duracion: formatTime(totalElapsedTime),
              tiempoAcumulado: formatTime(accumulatedTime),
              distancia: ((lap.total_distance || 0) / 1000).toFixed(2),
              ritmoPromedio: calculatePace(speedKmh),
              ritmoMaximo: calculatePace((lap.enhanced_max_speed || 0) * 3.6),
              calorias: lap.total_calories || 0,
              ascenso: lap.total_ascent || 0,
              descenso: lap.total_descent || 0,
              frecuenciaCardiacaPromedio: lap.avg_heart_rate || 0,
              frecuenciaCardiacaMaxima: lap.max_heart_rate || 0,
              velocidadPromedio: speedKmh.toFixed(2),
              velocidadMaxima: ((lap.enhanced_max_speed || 0) * 3.6).toFixed(2),
              cadenciaPromedio: lap.avg_cadence || 0,
              temperaturaPromedio: lap.avg_temperature || 0,
              temperaturaMaxima: lap.max_temperature || 0,
            };
          });
          setLaps(lapsData);
        }
      } catch (err) {
        console.error('❌ Error al obtener datos de parsed-data:', err.message);
      }
    };
    if (fileId) fetchData();
  }, [fileId, calculatePace]);

  const fcLapChartData = {
    labels: laps.map((_, i) => `Lap ${i + 1}`),
    datasets: [
      {
        label: t('elevationProfile.hrLapSeriesLabel'),
        data: laps.map(l => l.frecuenciaCardiacaPromedio),
        borderColor: 'rgba(255,165,0,1)',
        backgroundColor: 'rgba(255,165,0,0.15)',
        borderWidth: 2,
        tension: 0.3,
        pointRadius: 3,
        pointBackgroundColor: 'rgba(255,165,0,1)',
        fill: true,
      },
    ],
  };

  const fcLapChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (c) => ` FC: ${c.raw} bpm` } },
    },
    scales: {
      x: { ticks: { font: { size: 8 }, maxTicksLimit: 12 } },
      y: { ticks: { font: { size: 9 } }, title: { display: true, text: t('elevationProfile.axisBpm'), font: { size: 9 } } },
    },
  };

  return (
    <div className="graphics-container">
      {laps.length > 0 && (
        <>
          <LapsTable laps={laps} />
          <div style={{ marginTop: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#193255', marginBottom: 6 }}>
              {t('elevationProfile.hrLapHistogramTitle')}
            </div>
            <Line data={fcLapChartData} options={fcLapChartOptions} height={110} />
          </div>
        </>
      )}
    </div>
  );
};

export default Graphics;
